import type { NamedProjectRepository } from '../persistence/index.ts';
import type { ReadOnlyShare } from '../platform/index.ts';
import { sha256Canonical, type CoasterProject } from '../schema/index.ts';

export interface CapturedHeroWorkspace {
  readonly visualFingerprint: string;
  readonly cameraFingerprint: string;
  readonly capturedAt: string;
}

export interface CreatorPublishWorkspaceV1 {
  readonly schemaVersion: 1;
  readonly projectId: string;
  readonly ownerId: string;
  readonly lastCloudVersion: number | null;
  readonly lastSyncedProjectFingerprint: string | null;
  readonly capturedHero: CapturedHeroWorkspace | null;
  readonly share: ReadOnlyShare | null;
  readonly shareProjectFingerprint: string | null;
  readonly updatedAt: string;
}

export interface PublishConflictState {
  readonly projectId: string;
  readonly currentVersion: number;
  readonly detectedAt: string;
}

export type PublishHydrationState = 'idle' | 'loading' | 'ready' | 'recoverable_error';

function workspaceKey(ownerId: string, projectId: string): string {
  return `publish-workspace:${ownerId}:${projectId}`;
}

function isReadOnlyShare(value: unknown, projectId: string): value is ReadOnlyShare {
  if (!value || typeof value !== 'object') return false;
  const share = value as Partial<ReadOnlyShare>;
  return (
    share.projectId === projectId &&
    typeof share.shareId === 'string' &&
    typeof share.secret === 'string' &&
    typeof share.path === 'string' &&
    typeof share.revisionId === 'string' &&
    typeof share.createdAt === 'string'
  );
}

function isCapturedHero(value: unknown): value is CapturedHeroWorkspace {
  if (!value || typeof value !== 'object') return false;
  const hero = value as Partial<CapturedHeroWorkspace>;
  return (
    typeof hero.visualFingerprint === 'string' &&
    typeof hero.cameraFingerprint === 'string' &&
    typeof hero.capturedAt === 'string'
  );
}

function parseWorkspace(
  value: unknown,
  ownerId: string,
  projectId: string,
): CreatorPublishWorkspaceV1 | null {
  if (!value || typeof value !== 'object') return null;
  const workspace = value as Partial<CreatorPublishWorkspaceV1>;
  if (
    workspace.schemaVersion !== 1 ||
    workspace.ownerId !== ownerId ||
    workspace.projectId !== projectId ||
    typeof workspace.updatedAt !== 'string' ||
    (workspace.lastCloudVersion !== null &&
      (!Number.isInteger(workspace.lastCloudVersion) || workspace.lastCloudVersion! < 1)) ||
    (workspace.lastSyncedProjectFingerprint !== null &&
      typeof workspace.lastSyncedProjectFingerprint !== 'string') ||
    (workspace.capturedHero !== null && !isCapturedHero(workspace.capturedHero)) ||
    (workspace.share !== null &&
      workspace.share !== undefined &&
      !isReadOnlyShare(workspace.share, projectId)) ||
    (workspace.shareProjectFingerprint !== null &&
      workspace.shareProjectFingerprint !== undefined &&
      typeof workspace.shareProjectFingerprint !== 'string')
  )
    return null;
  return {
    ...(workspace as CreatorPublishWorkspaceV1),
    share: workspace.share ?? null,
    shareProjectFingerprint: workspace.shareProjectFingerprint ?? null,
  };
}

export class CreatorPublishWorkspaceRepository {
  constructor(
    private readonly repository: NamedProjectRepository,
    private readonly ownerId: string,
  ) {}

  async load(projectId: string): Promise<CreatorPublishWorkspaceV1 | null> {
    const result = await this.repository.getPreference<unknown>(
      workspaceKey(this.ownerId, projectId),
    );
    if (!result.ok) throw new Error(result.error.message);
    if (result.value === null) return null;
    const parsed = parseWorkspace(result.value, this.ownerId, projectId);
    if (parsed) return parsed;
    await this.clear(projectId);
    return null;
  }

  async save(workspace: CreatorPublishWorkspaceV1): Promise<void> {
    if (workspace.ownerId !== this.ownerId)
      throw new Error('Publish workspace ownership does not match the current viewer.');
    const parsed = parseWorkspace(workspace, this.ownerId, workspace.projectId);
    if (!parsed) throw new Error('Publish workspace data is invalid.');
    const result = await this.repository.setPreference(
      workspaceKey(this.ownerId, workspace.projectId),
      parsed,
    );
    if (!result.ok) throw new Error(result.error.message);
  }

  async clear(projectId: string): Promise<void> {
    const result = await this.repository.setPreference(workspaceKey(this.ownerId, projectId), null);
    if (!result.ok) throw new Error(result.error.message);
  }
}

export function createEmptyPublishWorkspace(
  ownerId: string,
  projectId: string,
): CreatorPublishWorkspaceV1 {
  return {
    schemaVersion: 1,
    projectId,
    ownerId,
    lastCloudVersion: null,
    lastSyncedProjectFingerprint: null,
    capturedHero: null,
    share: null,
    shareProjectFingerprint: null,
    updatedAt: new Date().toISOString(),
  };
}

export async function createCloudSyncFingerprint(project: CoasterProject): Promise<string> {
  return sha256Canonical({
    schemaVersion: project.schemaVersion,
    projectId: project.projectId,
    revisionId: project.revisionId,
    coasterFamilyId: project.coasterFamilyId,
    metadata: {
      title: project.metadata.title,
      description: project.metadata.description,
      tags: project.metadata.tags,
    },
    plot: project.plot,
    track: project.track,
    train: project.train,
    appearance: project.appearance,
    supports: project.supports,
    scenery: project.scenery,
    highlights: project.highlights,
    permissions: project.permissions,
    heroCamera: project.heroCamera,
    lineage: project.lineage,
  });
}

export async function createVisualProjectFingerprint(project: CoasterProject): Promise<string> {
  return sha256Canonical({
    coasterFamilyId: project.coasterFamilyId,
    plot: project.plot,
    track: project.track,
    train: project.train,
    appearance: project.appearance,
    supports: project.supports,
    scenery: project.scenery,
    highlights: project.highlights,
  });
}

export async function createHeroCameraFingerprint(
  camera: CoasterProject['heroCamera'],
): Promise<string> {
  return sha256Canonical(camera);
}
